import { useState, useEffect } from "react";
import Layout from "../components/Layout.jsx";
import { config as configApi, getAccount, masterSupabase } from "../lib/supabase.js";
import { useUserRole, hasPermission } from "../App.jsx";
import IntegrationsSection from "../components/IntegrationsSection.jsx";

// ─── Fonts ───────────────────────────────────────────────────────────────────

function useFonts() {
  useEffect(() => {
    [{ id: "gfont-instrument-serif", href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap" },
     { id: "gfont-dm-sans", href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap" }
    ].forEach(f => { if (!document.getElementById(f.id)) { const link = document.createElement("link"); link.id = f.id; link.rel = "stylesheet"; link.href = f.href; document.head.appendChild(link); } });
  }, []);
}

// ─── Theme tokens ────────────────────────────────────────────────────────────

const L = { bg: "#fafaf8", text: "#1a1a1a", textSoft: "#444444", muted: "#8a8a8a", dim: "#c0bdb8", border: "#e5e3de", borderLight: "#f0eee9", surface: "#ffffff", inputBg: "#f6f5f2" };
const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";
const iS = { width: "100%", padding: "9px 14px", background: L.inputBg, border: `1px solid ${L.border}`, borderRadius: 8, color: L.text, fontSize: 13, outline: "none", fontFamily: sans, boxSizing: "border-box" };
const lS = { display: "block", fontSize: 10, color: L.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5, fontWeight: 600 };
const secHead = (text) => <div style={{ fontSize: 11, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 18 }}>{text}</div>;
const btnGhost = { padding: "8px 16px", background: "none", border: `1px solid ${L.border}`, borderRadius: 8, color: L.muted, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: sans };
const btnPrimary = { padding: "10px 24px", background: L.text, border: "none", borderRadius: 8, color: L.bg, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans };
const card = { background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, padding: 26 };

function Toggle({ value, onChange, label, desc }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", background: L.inputBg, borderRadius: 10, border: `1px solid ${L.borderLight}` }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        {desc && <div style={{ fontSize: 12, color: L.muted, marginTop: 2 }}>{desc}</div>}
      </div>
      <button onClick={() => onChange(!value)} style={{ width: 42, height: 24, borderRadius: 12, border: `1px solid ${value ? "#16a34a" : L.border}`, cursor: "pointer", background: value ? "#16a34a" : L.inputBg, position: "relative", transition: "all 0.2s" }}>
        <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: value ? 21 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }} />
      </button>
    </div>
  );
}

function FlashMsg({ msg }) {
  if (!msg) return null;
  return <div style={{ padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 12, background: msg.type === "error" ? "#fef2f2" : "#f0fdf4", color: msg.type === "error" ? "#dc2626" : "#15803d", border: `1px solid ${msg.type === "error" ? "#fecaca" : "#bbf7d0"}` }}>{msg.text}</div>;
}

// ─── Config constants ────────────────────────────────────────────────────────

const EMBEDDING_PROVIDERS = {
  openai: { name: "OpenAI", models: [{ id: "text-embedding-3-small", label: "text-embedding-3-small", desc: "Fast, cost-effective" }, { id: "text-embedding-3-large", label: "text-embedding-3-large", desc: "Higher quality" }], keyLabel: "OpenAI API Key", keyPlaceholder: "sk-proj-...", keyField: "credential_openai_key", docsUrl: "https://platform.openai.com/api-keys" },
  gemini: { name: "Google Gemini", models: [{ id: "gemini-embedding-001", label: "gemini-embedding-001", desc: "Latest, 3072 native (output 1024)" }, { id: "text-embedding-004", label: "text-embedding-004", desc: "Previous gen" }], keyLabel: "Gemini API Key", keyPlaceholder: "AIza...", keyField: "credential_gemini_key", docsUrl: "https://ai.google.dev/gemini-api/docs/api-key" },
  voyage: { name: "Voyage AI", models: [{ id: "voyage-3.5", label: "voyage-3.5", desc: "Best general-purpose" }, { id: "voyage-3.5-lite", label: "voyage-3.5-lite", desc: "Faster, lower cost" }, { id: "voyage-code-3", label: "voyage-code-3", desc: "Optimized for code" }], keyLabel: "Voyage API Key", keyPlaceholder: "pa-...", keyField: "credential_voyage_key", docsUrl: "https://docs.voyageai.com/docs/api-key-and-installation" },
};
const CHAT_PROVIDERS = {
  openai: { name: "OpenAI", models: ["gpt-4o-mini", "gpt-4o", "gpt-4-turbo"] },
  gemini: { name: "Google Gemini", models: ["gemini-2.0-flash", "gemini-1.5-pro"] },
  anthropic: { name: "Anthropic", models: ["claude-sonnet-4-5-20250929", "claude-haiku-3-5-20241022"] },
};

// ─── Custom Domain ───────────────────────────────────────────────────────────
// Works with updated /api/domains that returns:
//   { domain, status, ownership_verified, dns_configured, dns_instructions[] }
// Status values: "active", "pending_dns", "pending_verification"

function DnsRecord({ rec }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "54px 1fr", gap: "4px 10px", padding: "10px 14px", background: L.surface, borderRadius: 8, border: `1px solid ${L.borderLight}`, marginBottom: 6, fontSize: 12 }}>
      <span style={{ color: L.muted, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Type</span>
      <span style={{ fontFamily: "monospace", fontWeight: 600, color: L.text }}>{rec.type}</span>
      <span style={{ color: L.muted, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Name</span>
      <span style={{ fontFamily: "monospace", color: L.textSoft, wordBreak: "break-all" }}>{rec.name}</span>
      <span style={{ color: L.muted, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Value</span>
      <span style={{ fontFamily: "monospace", color: L.textSoft, wordBreak: "break-all" }}>{rec.value}</span>
      {rec.purpose && <>
        <span style={{ color: L.muted, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Why</span>
        <span style={{ color: L.muted, fontSize: 11 }}>{rec.purpose}</span>
      </>}
    </div>
  );
}

function CustomDomainSection({ account }) {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState(null);

  const getToken = async () => {
    const { data: { session } } = await masterSupabase.auth.getSession();
    return session?.access_token;
  };

  const loadDomains = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/domains", { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setDomains(data.domains || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadDomains(); }, []);

  const handleAdd = async () => {
    if (!newDomain.trim()) return;
    setAdding(true);
    setMessage(null);
    try {
      const token = await getToken();
      const res = await fetch("/api/domains", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ domain: newDomain.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      if (data.status === "active") {
        setMessage({ type: "success", text: `${data.domain} is verified and active.` });
      } else {
        setMessage({ type: "info", text: `${data.domain} added — configure the DNS records below.` });
      }
      setNewDomain("");
      loadDomains();
    } catch (e) { setMessage({ type: "error", text: e.message }); }
    setAdding(false);
  };

  const handleCheck = async () => {
    setChecking(true);
    setMessage(null);
    await loadDomains();
    setChecking(false);
  };

  const handleRemove = async (domain) => {
    if (!confirm(`Remove ${domain}? This will also remove it from Vercel.`)) return;
    try {
      const token = await getToken();
      const res = await fetch("/api/domains", {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ domain }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setMessage({ type: "success", text: `${domain} removed.` });
      loadDomains();
    } catch (e) { setMessage({ type: "error", text: e.message }); }
  };

  const tierAllowed = account?.plan === "enterprise" || account?.plan === "pro";

  const statusBadge = (status) => {
    const map = {
      active:               { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", label: "Active" },
      pending_dns:          { bg: "#fffbeb", color: "#b45309", border: "#fde68a", label: "DNS pending" },
      pending_verification: { bg: "#fffbeb", color: "#b45309", border: "#fde68a", label: "Verifying" },
      pending:              { bg: "#fffbeb", color: "#b45309", border: "#fde68a", label: "Pending" },
    };
    return map[status] || map.pending;
  };

  const stepDot = (done, num) => ({
    width: 16, height: 16, borderRadius: "50%", display: "inline-flex", alignItems: "center",
    justifyContent: "center", fontSize: 9, fontWeight: 700,
    background: done ? "#f0fdf4" : "#fffbeb", color: done ? "#15803d" : "#b45309",
    border: `1px solid ${done ? "#bbf7d0" : "#fde68a"}`,
  });

  return (
    <div>
      {secHead("Custom domain")}
      {!tierAllowed ? (
        <div style={{ padding: "16px 20px", background: L.inputBg, borderRadius: 10, border: `1px solid ${L.borderLight}`, fontSize: 13, color: L.textSoft, lineHeight: 1.65 }}>
          Custom domains are available on Pro and Enterprise plans. Upgrade to use your own domain for your team's dashboard.
        </div>
      ) : (<>
        <p style={{ fontSize: 13, color: L.muted, margin: "0 0 16px", lineHeight: 1.65 }}>
          Use your own domain for the tenant dashboard. Your team logs in at your custom domain instead of app.clarifico.io.
        </p>
        <FlashMsg msg={message} />
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <input value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="portal.yourcompany.com" style={{ ...iS, flex: 1, fontFamily: "monospace", fontSize: 12 }} onKeyDown={e => e.key === "Enter" && handleAdd()} />
          <button onClick={handleAdd} disabled={adding || !newDomain.trim()} style={{ ...btnPrimary, opacity: (adding || !newDomain.trim()) ? 0.3 : 1, fontSize: 12 }}>{adding ? "Adding..." : "Add domain"}</button>
        </div>

        {loading ? (
          <div style={{ padding: 20, color: L.muted, fontSize: 12, textAlign: "center" }}>Loading...</div>
        ) : domains.length === 0 ? (
          <div style={{ padding: "16px 20px", background: L.inputBg, borderRadius: 10, fontSize: 12, color: L.muted, textAlign: "center" }}>No custom domains configured.</div>
        ) : domains.map(d => {
          const badge = statusBadge(d.status);
          const isPending = d.status !== "active";
          const instructions = d.dns_instructions || [];

          return (
            <div key={d.id || d.domain} style={{ padding: "16px 18px", background: L.inputBg, borderRadius: 10, border: `1px solid ${d.status === "active" ? "#bbf7d0" : L.borderLight}`, marginBottom: 10 }}>
              {/* Domain header row */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 500, fontFamily: "monospace" }}>{d.domain}</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", background: badge.bg, color: badge.color, border: `1px solid ${badge.border}` }}>{badge.label}</span>
                  <button onClick={() => handleRemove(d.domain)} style={btnGhost}>Remove</button>
                </div>
              </div>

              {/* Active state */}
              {d.status === "active" && (
                <div style={{ fontSize: 12, color: "#15803d", lineHeight: 1.6 }}>
                  Domain is verified and serving traffic. Your team can log in at <strong>https://{d.domain}</strong>
                </div>
              )}

              {/* Pending states */}
              {isPending && (
                <div style={{ marginTop: 4 }}>
                  {/* Progress checklist */}
                  <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                      <span style={stepDot(d.ownership_verified, 1)}>{d.ownership_verified ? "\u2713" : "1"}</span>
                      <span style={{ color: d.ownership_verified ? "#15803d" : L.textSoft }}>Ownership {d.ownership_verified ? "verified" : "pending"}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                      <span style={stepDot(d.dns_configured, 2)}>{d.dns_configured ? "\u2713" : "2"}</span>
                      <span style={{ color: d.dns_configured ? "#15803d" : L.textSoft }}>DNS {d.dns_configured ? "configured" : "pending"}</span>
                    </div>
                  </div>

                  {/* DNS record instructions */}
                  {instructions.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, color: "#b45309", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                        Add {instructions.length === 1 ? "this record" : "these records"} in your DNS provider
                      </div>
                      {instructions.map((rec, i) => <DnsRecord key={i} rec={rec} />)}
                      <p style={{ fontSize: 11, color: L.dim, margin: "8px 0 0", lineHeight: 1.6 }}>
                        DNS changes typically take 5 minutes to 1 hour to propagate. Vercel will automatically provision an SSL certificate once DNS is verified.
                      </p>
                    </div>
                  )}

                  {/* Fallback if no instructions returned */}
                  {instructions.length === 0 && (
                    <div style={{ fontSize: 12, color: L.muted, lineHeight: 1.6 }}>
                      Waiting for DNS configuration. Add a CNAME record pointing <strong>{d.domain}</strong> to <strong>cname.vercel-dns.com</strong>, then check verification.
                    </div>
                  )}

                  {/* Check verification button */}
                  <button onClick={handleCheck} disabled={checking} style={{ ...btnGhost, marginTop: 12, fontSize: 11 }}>{checking ? "Checking..." : "Check verification"}</button>
                </div>
              )}
            </div>
          );
        })}
      </>)}
    </div>
  );
}

// ─── Tab definitions ─────────────────────────────────────────────────────────

const TABS = [
  { key: "appearance", label: "Appearance" },
  { key: "ai", label: "AI Configuration" },
  { key: "keys", label: "API Keys" },
  { key: "floor", label: "Floor Access" },
  { key: "infra", label: "Domain & Integrations" },
  { key: "account", label: "Account" },
];

// ─── Main component ──────────────────────────────────────────────────────────

export default function Settings() {
  useFonts();
  const account = getAccount();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reEmbedding, setReEmbedding] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [activeTab, setActiveTab] = useState("appearance");

  const [embeddingProvider, setEmbeddingProvider] = useState("openai");
  const [embeddingModel, setEmbeddingModel] = useState("text-embedding-3-small");
  const [chatProvider, setChatProvider] = useState("openai");
  const [chatModel, setChatModel] = useState("gpt-4o-mini");
  const [credentials, setCredentials] = useState({ credential_openai_key: "", credential_gemini_key: "", credential_voyage_key: "", credential_anthropic_key: "" });
  const [orgContext, setOrgContext] = useState("");
  const [customInstructions, setCustomInstructions] = useState("");

  const [floorEnabled, setFloorEnabled] = useState(false);
  const [floorPin, setFloorPin] = useState("1234");
  const [floorIdleTimeout, setFloorIdleTimeout] = useState(5);
  const [floorKioskAuto, setFloorKioskAuto] = useState(false);
  const [floorTitle, setFloorTitle] = useState("Document Access");
  const [floorGreeting, setFloorGreeting] = useState("Search manuals or ask a question");

  const [theme, setTheme] = useState({ primary_color: "#6366f1", sidebar_bg: "#0d0d14", sidebar_text: "#e0e0e0", main_bg: "#0b0b12", card_bg: "#111118", card_border: "#1e1e2e", logo_url: "", logo_width: 140, favicon_url: "", font_family: "Sora" });
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeMessage, setThemeMessage] = useState(null);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);

  const { role: currentUserRole } = useUserRole();
  const canManageSettings = hasPermission(currentUserRole, "manageSettings");
  const canManageApiKeys = hasPermission(currentUserRole, "manageApiKeys");
  const platformUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  useEffect(() => {
    (async () => {
      const { data } = await configApi.get();
      if (data) {
        setCfg(data); setEmbeddingProvider(data.embedding_provider || "openai"); setEmbeddingModel(data.embedding_model || "text-embedding-3-small");
        setChatProvider(data.ai_provider || "openai"); setChatModel(data.ai_model || "gpt-4o-mini");
        setOrgContext(data.org_context || ""); setCustomInstructions(data.custom_instructions || "");
        setCredentials({ credential_openai_key: data.credential_openai_key || "", credential_gemini_key: data.credential_gemini_key || "", credential_voyage_key: data.credential_voyage_key || "", credential_anthropic_key: data.credential_anthropic_key || "" });
        setFloorEnabled(data.floor_access_enabled || false); setFloorPin(data.floor_access_pin || "1234");
        setFloorIdleTimeout(data.floor_access_idle_timeout || 5); setFloorKioskAuto(data.floor_access_kiosk_auto || false);
        setFloorTitle(data.floor_access_title || "Document Access"); setFloorGreeting(data.floor_access_greeting || "Search manuals or ask a question");
      }
      setLoading(false);
    })();
    if (account) setTheme({ primary_color: account.theme_primary_color || account.widget_color || "#6366f1", sidebar_bg: account.theme_sidebar_bg || "#0d0d14", sidebar_text: account.theme_sidebar_text || "#e0e0e0", main_bg: account.theme_main_bg || "#0b0b12", card_bg: account.theme_card_bg || "#111118", card_border: account.theme_card_border || "#1e1e2e", logo_url: account.theme_logo_url || "", logo_width: account.theme_logo_width || 140, favicon_url: account.theme_favicon_url || "", font_family: account.theme_font_family || "Sora" });
  }, []);

  const handleProviderChange = (p) => { setEmbeddingProvider(p); const m = EMBEDDING_PROVIDERS[p]?.models[0]?.id; if (m) setEmbeddingModel(m); };

  const handleThemeSave = async () => {
    if (!canManageSettings) return; setThemeSaving(true); setThemeMessage(null);
    const { error } = await masterSupabase.from("accounts").update({ theme_primary_color: theme.primary_color, theme_sidebar_bg: theme.sidebar_bg, theme_sidebar_text: theme.sidebar_text, theme_main_bg: theme.main_bg, theme_card_bg: theme.card_bg, theme_card_border: theme.card_border, theme_logo_url: theme.logo_url || null, theme_logo_width: theme.logo_width, theme_favicon_url: theme.favicon_url || null, theme_font_family: theme.font_family, widget_color: theme.primary_color, updated_at: new Date().toISOString() }).eq("id", account.id);
    if (error) setThemeMessage({ type: "error", text: error.message }); else setThemeMessage({ type: "success", text: "Theme saved! Reload to see changes." });
    setThemeSaving(false);
  };

  const handlePasswordChange = async () => {
    setPasswordMessage(null);
    if (newPassword.length < 8) { setPasswordMessage({ type: "error", text: "Password must be at least 8 characters." }); return; }
    if (newPassword !== confirmPassword) { setPasswordMessage({ type: "error", text: "Passwords don't match." }); return; }
    setPasswordSaving(true);
    const { error } = await masterSupabase.auth.updateUser({ password: newPassword });
    if (error) setPasswordMessage({ type: "error", text: error.message }); else { setPasswordMessage({ type: "success", text: "Password updated." }); setNewPassword(""); setConfirmPassword(""); }
    setPasswordSaving(false);
  };

  const handleSave = async () => {
    if (!canManageSettings) return; setSaving(true); setSaveMessage(null);
    const provChanged = cfg && cfg.embedding_provider !== embeddingProvider;
    const modChanged = cfg && cfg.embedding_model !== embeddingModel;
    const { error } = await configApi.update({ embedding_provider: embeddingProvider, embedding_model: embeddingModel, embedding_dimensions: 1024, ai_provider: chatProvider, ai_model: chatModel, org_context: orgContext, custom_instructions: customInstructions, ...credentials, floor_access_enabled: floorEnabled, floor_access_pin: floorPin, floor_access_idle_timeout: floorIdleTimeout, floor_access_kiosk_auto: floorKioskAuto, floor_access_title: floorTitle, floor_access_greeting: floorGreeting });
    if (error) setSaveMessage({ type: "error", text: error.message });
    else { setCfg(prev => ({ ...prev, embedding_provider: embeddingProvider, embedding_model: embeddingModel })); setSaveMessage({ type: "success", text: (provChanged || modChanged) ? "Saved. Embedding model changed — re-embed your documents." : "Settings saved." }); }
    setSaving(false);
  };

  const handleReEmbed = async () => {
    if (!canManageSettings || !confirm("Regenerate all embeddings? This uses API credits.")) return;
    setReEmbedding(true); setSaveMessage(null);
    try { const res = await fetch(`${platformUrl}/functions/v1/re-embed?account=${account.slug}`, { method: "POST", headers: { Authorization: `Bearer ${anonKey}`, "Content-Type": "application/json" }, body: "{}" }); const result = await res.json(); if (!res.ok || result.error) throw new Error(result.error || "Failed"); setSaveMessage({ type: "success", text: `Re-embedded: ${result.chunks_processed} chunks across ${result.documents_processed} docs.` }); } catch (e) { setSaveMessage({ type: "error", text: e.message }); }
    setReEmbedding(false);
  };

  if (loading) return <Layout><div style={{ padding: 40, color: L.muted, textAlign: "center", background: L.bg, margin: -28, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: sans }}>Loading...</div></Layout>;

  const currentEmbedProvider = EMBEDDING_PROVIDERS[embeddingProvider];
  const providerChanged = cfg && cfg.embedding_provider !== embeddingProvider;
  const modelChanged = cfg && cfg.embedding_model !== embeddingModel;
  const floorUrl = `${window.location.origin}/floor?account=${account?.slug || ""}`;

  // Filter tabs by permission
  const visibleTabs = TABS.filter(t => {
    if (t.key === "appearance" && !canManageSettings) return false;
    if (t.key === "ai" && !canManageSettings) return false;
    if (t.key === "keys" && !canManageApiKeys) return false;
    if (t.key === "floor" && !canManageSettings) return false;
    if (t.key === "infra" && !canManageApiKeys) return false;
    return true;
  });

  return (
    <Layout>
      <style>{"@keyframes ov-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }"}</style>

      <div style={{ minHeight: "calc(100vh - 56px)", background: L.bg, margin: -28, padding: "40px 44px", fontFamily: sans, color: L.text }}>
        {/* Header */}
        <div style={{ marginBottom: 28, animation: "ov-up 0.5s ease both" }}>
          <h1 style={{ fontSize: 36, fontWeight: 400, lineHeight: 1.1, margin: "0 0 8px", fontFamily: serif, fontStyle: "italic" }}>Settings</h1>
          <p style={{ margin: 0, fontSize: 13, color: L.muted }}>AI configuration, credentials, and platform management</p>
        </div>

        {!canManageSettings && (
          <div style={{ padding: "10px 16px", borderRadius: 8, marginBottom: 18, fontSize: 12, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}>
            You have <strong>{currentUserRole}</strong> access. Most settings require admin or owner access.
          </div>
        )}

        <FlashMsg msg={saveMessage} />

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 28, flexWrap: "wrap", animation: "ov-up 0.5s ease 0.05s both" }}>
          {visibleTabs.map(t => {
            const active = activeTab === t.key;
            return (
              <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                padding: "8px 16px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                cursor: "pointer", fontFamily: sans, transition: "all 0.15s",
                background: active ? L.text : "none",
                color: active ? L.bg : L.muted,
                border: `1px solid ${active ? L.text : L.border}`,
              }}>{t.label}</button>
            );
          })}
        </div>

        <div style={{ animation: "ov-up 0.5s ease 0.08s both" }}>

          {/* ═══ APPEARANCE ═══ */}
          {activeTab === "appearance" && canManageSettings && (
            <div style={card}>
              {secHead("Dashboard theme")}
              <FlashMsg msg={themeMessage} />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
                {[
                  { key: "primary_color", label: "Primary / accent" }, { key: "sidebar_bg", label: "Sidebar background" },
                  { key: "sidebar_text", label: "Sidebar text" }, { key: "main_bg", label: "Main background" },
                  { key: "card_bg", label: "Card background" }, { key: "card_border", label: "Card border" },
                ].map(c => (
                  <div key={c.key}>
                    <label style={lS}>{c.label}</label>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input type="color" value={theme[c.key] || "#000"} onChange={e => setTheme(p => ({ ...p, [c.key]: e.target.value }))} style={{ width: 34, height: 34, border: `1px solid ${L.border}`, borderRadius: 6, cursor: "pointer", background: "none", padding: 2 }} />
                      <input value={theme[c.key] || ""} onChange={e => setTheme(p => ({ ...p, [c.key]: e.target.value }))} style={{ ...iS, flex: 1, fontFamily: "monospace", fontSize: 12 }} />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
                {[
                  { label: "Reset default", fn: () => setTheme(p => ({ ...p, primary_color: "#6366f1", sidebar_bg: "#0d0d14", sidebar_text: "#e0e0e0", main_bg: "#0b0b12", card_bg: "#111118", card_border: "#1e1e2e", font_family: "Sora" })) },
                  { label: "MORryde Blue", fn: () => setTheme(p => ({ ...p, primary_color: "#0154a4", sidebar_bg: "#001a3d", sidebar_text: "#fff", main_bg: "#0a1628", card_bg: "#0f1f38", card_border: "#1a3050" })) },
                  { label: "Warm Red", fn: () => setTheme(p => ({ ...p, primary_color: "#e63946", sidebar_bg: "#1a0a0a", sidebar_text: "#e0e0e0", main_bg: "#120808", card_bg: "#1a1012", card_border: "#2e1a1e" })) },
                ].map(p => <button key={p.label} onClick={p.fn} style={{ ...btnGhost, fontSize: 11, padding: "6px 12px" }}>{p.label}</button>)}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
                <div><label style={lS}>Logo URL</label><input value={theme.logo_url} onChange={e => setTheme(p => ({ ...p, logo_url: e.target.value }))} placeholder="https://yoursite.com/logo.png" style={iS} />{theme.logo_url && <img src={theme.logo_url} alt="" style={{ maxHeight: 36, marginTop: 8, borderRadius: 4 }} onError={e => { e.target.style.display = "none"; }} />}</div>
                <div><label style={lS}>Favicon URL</label><input value={theme.favicon_url} onChange={e => setTheme(p => ({ ...p, favicon_url: e.target.value }))} placeholder="https://yoursite.com/favicon.ico" style={iS} /></div>
                <div><label style={lS}>Logo width (px)</label><input type="number" value={theme.logo_width} onChange={e => setTheme(p => ({ ...p, logo_width: parseInt(e.target.value) || 140 }))} style={{ ...iS, width: 100 }} /></div>
                <div><label style={lS}>Font family</label><select value={theme.font_family} onChange={e => setTheme(p => ({ ...p, font_family: e.target.value }))} style={{ ...iS, cursor: "pointer" }}>{["Sora", "Inter", "Poppins", "DM Sans", "Nunito", "Roboto", "Open Sans", "Lato", "Montserrat"].map(f => <option key={f} value={f}>{f}</option>)}</select></div>
              </div>

              {/* Live preview */}
              <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${theme.card_border}`, marginBottom: 20 }}>
                <div style={{ padding: "8px 14px", background: theme.sidebar_bg, color: theme.sidebar_text, fontSize: 11, fontWeight: 600, fontFamily: `'${theme.font_family}', sans-serif` }}>{theme.logo_url ? <img src={theme.logo_url} alt="" style={{ maxHeight: 18 }} onError={e => { e.target.textContent = "Preview"; }} /> : (account?.name || "Preview")}</div>
                <div style={{ padding: 14, background: theme.main_bg }}><div style={{ padding: "8px 12px", background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: 6, fontSize: 10, color: theme.sidebar_text, marginBottom: 6 }}>Card preview</div><div style={{ display: "flex", gap: 6 }}><div style={{ height: 4, flex: 3, background: theme.primary_color, borderRadius: 2 }} /><div style={{ height: 4, flex: 1, background: theme.card_border, borderRadius: 2 }} /></div></div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button onClick={handleThemeSave} disabled={themeSaving} style={{ ...btnPrimary, opacity: themeSaving ? 0.5 : 1 }}>{themeSaving ? "Saving..." : "Save theme"}</button>
              </div>
            </div>
          )}

          {/* ═══ AI CONFIGURATION ═══ */}
          {activeTab === "ai" && canManageSettings && (<>
            {/* Brief */}
            <div style={{ padding: "18px 0 16px", borderBottom: `1px solid ${L.border}`, marginBottom: 28 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: L.muted, marginBottom: 10 }}>The brief</div>
              <div style={{ fontSize: 14, lineHeight: 1.75, color: L.textSoft, maxWidth: 720 }}>
                These settings control how your AI processes documents and responds to visitors. The embedding model determines how documents are indexed for search. The chat model determines how the AI generates responses. Changing the embedding model requires re-processing all documents.
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
              {/* Left: Embedding + Re-embed */}
              <div>
                <div style={{ ...card, marginBottom: 18 }}>
                  {secHead("Embedding engine")}
                  <p style={{ fontSize: 13, color: L.muted, margin: "0 0 16px", lineHeight: 1.65 }}>Converts your documents into searchable vectors. All providers output 1024 dimensions, normalized for compatibility.</p>
                  <label style={lS}>Provider</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                    {Object.entries(EMBEDDING_PROVIDERS).map(([key, prov]) => { const active = embeddingProvider === key; return <button key={key} onClick={() => handleProviderChange(key)} style={{ flex: 1, padding: "12px 10px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: sans, textAlign: "center", background: active ? L.text : "none", color: active ? L.bg : L.muted, border: `1px solid ${active ? L.text : L.border}`, transition: "all 0.15s" }}>{prov.name}</button>; })}
                  </div>
                  <label style={lS}>Model</label>
                  {currentEmbedProvider?.models.map(m => (
                    <button key={m.id} onClick={() => setEmbeddingModel(m.id)} style={{ display: "block", width: "100%", padding: "12px 16px", borderRadius: 8, textAlign: "left", cursor: "pointer", fontFamily: sans, marginBottom: 6, background: embeddingModel === m.id ? L.inputBg : L.surface, border: `1px solid ${embeddingModel === m.id ? L.text : L.borderLight}`, transition: "border-color 0.15s" }}>
                      <div style={{ fontSize: 13, fontWeight: embeddingModel === m.id ? 600 : 400, fontFamily: "monospace" }}>{m.label}</div>
                      <div style={{ fontSize: 11, color: L.muted, marginTop: 2 }}>{m.desc}</div>
                    </button>
                  ))}
                  {(providerChanged || modelChanged) && <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 12, color: "#b45309" }}>Embedding {providerChanged ? "provider" : "model"} changed. Save, then re-embed your documents.</div>}
                </div>

                <div style={card}>
                  {secHead("Re-embed documents")}
                  <p style={{ fontSize: 13, color: L.muted, margin: "0 0 14px", lineHeight: 1.65 }}>Regenerate all document embeddings using the current provider. Required after changing the embedding model.</p>
                  {cfg?.embedding_status === "re-embedding" && <div style={{ fontSize: 12, color: "#b45309", marginBottom: 10 }}>Re-embedding in progress...</div>}
                  {cfg?.last_embedded_at && <div style={{ fontSize: 11, color: L.dim, marginBottom: 10 }}>Last processed: {new Date(cfg.last_embedded_at).toLocaleString()}</div>}
                  <button onClick={handleReEmbed} disabled={reEmbedding} style={{ ...btnGhost, opacity: reEmbedding ? 0.5 : 1 }}>{reEmbedding ? "Re-embedding..." : "Re-embed all documents"}</button>
                </div>
              </div>

              {/* Right: Chat model + Organization context */}
              <div>
                <div style={{ ...card, marginBottom: 18 }}>
                  {secHead("Chat model")}
                  <p style={{ fontSize: 13, color: L.muted, margin: "0 0 16px", lineHeight: 1.65 }}>Generates responses to visitor questions using your documents as context.</p>
                  <label style={lS}>Provider</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                    {Object.entries(CHAT_PROVIDERS).map(([key, prov]) => { const active = chatProvider === key; return <button key={key} onClick={() => { setChatProvider(key); setChatModel(prov.models[0]); }} style={{ flex: 1, padding: "10px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: sans, background: active ? L.text : "none", color: active ? L.bg : L.muted, border: `1px solid ${active ? L.text : L.border}` }}>{prov.name}</button>; })}
                  </div>
                  <label style={lS}>Model</label>
                  <select value={chatModel} onChange={e => setChatModel(e.target.value)} style={{ ...iS, cursor: "pointer" }}>{CHAT_PROVIDERS[chatProvider]?.models.map(m => <option key={m} value={m}>{m}</option>)}</select>
                </div>

                <div style={card}>
                  {secHead("Organization context")}
                  <p style={{ fontSize: 13, color: L.muted, margin: "0 0 16px", lineHeight: 1.65 }}>This information is included in every AI response to help the model understand your business.</p>
                  <div style={{ marginBottom: 14 }}><label style={lS}>About your organization</label><textarea value={orgContext} onChange={e => setOrgContext(e.target.value)} placeholder="Describe your company, products, and services..." rows={4} style={{ ...iS, resize: "vertical", lineHeight: 1.6 }} /></div>
                  <div><label style={lS}>Custom instructions</label><textarea value={customInstructions} onChange={e => setCustomInstructions(e.target.value)} placeholder='e.g., "Always recommend scheduling a consultation..."' rows={3} style={{ ...iS, resize: "vertical", lineHeight: 1.6 }} /></div>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>{saving ? "Saving..." : "Save AI settings"}</button>
            </div>
          </>)}

          {/* ═══ API KEYS ═══ */}
          {activeTab === "keys" && canManageApiKeys && (
            <div style={card}>
              {secHead("API credentials")}
              <div style={{ padding: "14px 18px", background: L.inputBg, borderRadius: 10, border: `1px solid ${L.borderLight}`, marginBottom: 24 }}>
                <div style={{ fontSize: 13, color: L.textSoft, lineHeight: 1.65 }}>Add your own API keys to use your accounts directly. If a key is not set, the platform default is used. Keys are encrypted at rest.</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 20 }}>
                {Object.entries(EMBEDDING_PROVIDERS).map(([key, prov]) => (
                  <div key={key}>
                    <label style={lS}>{prov.keyLabel}{embeddingProvider === key && <span style={{ color: "#15803d", marginLeft: 6, fontSize: 9 }}>ACTIVE</span>}</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input type="password" value={credentials[prov.keyField]} onChange={e => setCredentials(p => ({ ...p, [prov.keyField]: e.target.value }))} placeholder={prov.keyPlaceholder} style={{ ...iS, flex: 1, fontFamily: "monospace", fontSize: 12 }} />
                      <a href={prov.docsUrl} target="_blank" rel="noopener" style={{ ...btnGhost, textDecoration: "none", display: "flex", alignItems: "center", fontSize: 11 }}>Get key</a>
                    </div>
                  </div>
                ))}
                <div>
                  <label style={lS}>Anthropic API Key{chatProvider === "anthropic" && <span style={{ color: "#15803d", marginLeft: 6, fontSize: 9 }}>ACTIVE</span>}</label>
                  <input type="password" value={credentials.credential_anthropic_key} onChange={e => setCredentials(p => ({ ...p, credential_anthropic_key: e.target.value }))} placeholder="sk-ant-..." style={{ ...iS, fontFamily: "monospace", fontSize: 12 }} />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>{saving ? "Saving..." : "Save keys"}</button>
              </div>
            </div>
          )}

          {/* ═══ FLOOR ACCESS ═══ */}
          {activeTab === "floor" && canManageSettings && (
            <div style={card}>
              {secHead("Floor access")}
              <p style={{ fontSize: 13, color: L.muted, margin: "0 0 20px", lineHeight: 1.65 }}>PIN-protected mobile access for your sales team. Designed for tablets and kiosks on showroom floors — staff scan QR codes to jump to specific documents.</p>
              <div style={{ marginBottom: 16 }}><Toggle value={floorEnabled} onChange={setFloorEnabled} label="Enable floor access" desc="Allow PIN-based access at /floor" /></div>
              {floorEnabled && (<>
                <div style={{ marginBottom: 14 }}><label style={lS}>Team PIN</label><input value={floorPin} onChange={e => setFloorPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="1234" maxLength={6} style={{ ...iS, fontFamily: "monospace", fontSize: 18, letterSpacing: "0.3em", textAlign: "center", width: 160 }} /><div style={{ fontSize: 11, color: L.dim, marginTop: 4 }}>4-6 digits</div></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                  <div><label style={lS}>Title</label><input value={floorTitle} onChange={e => setFloorTitle(e.target.value)} style={iS} /></div>
                  <div><label style={lS}>Greeting</label><input value={floorGreeting} onChange={e => setFloorGreeting(e.target.value)} style={iS} /></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                  <div><label style={lS}>Idle timeout (min)</label><input type="number" value={floorIdleTimeout} onChange={e => setFloorIdleTimeout(parseInt(e.target.value) || 5)} min={1} max={60} style={{ ...iS, width: 80 }} /></div>
                  <div><label style={lS}>Auto kiosk</label><Toggle value={floorKioskAuto} onChange={setFloorKioskAuto} label="Auto fullscreen" /></div>
                </div>
                <div style={{ padding: "12px 16px", background: L.inputBg, borderRadius: 10, border: `1px solid ${L.borderLight}` }}>
                  <div style={{ fontSize: 10, color: L.muted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 4 }}>Floor access URL</div>
                  <div style={{ fontSize: 12, color: "#6d28d9", fontFamily: "monospace", wordBreak: "break-all" }}>{floorUrl}</div>
                </div>
              </>)}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>{saving ? "Saving..." : "Save floor access"}</button>
              </div>
            </div>
          )}

          {/* ═══ DOMAIN & INTEGRATIONS ═══ */}
          {activeTab === "infra" && canManageApiKeys && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div style={card}>
                <CustomDomainSection account={account} />
              </div>
              <div style={card}>
                <IntegrationsSection color={account?.widget_color || "#6366f1"} account={account} />
              </div>
            </div>
          )}

          {/* ═══ ACCOUNT ═══ */}
          {activeTab === "account" && (
            <div style={card}>
              {secHead("Change password")}
              <div style={{ marginBottom: 14 }}><label style={lS}>New password</label><input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 characters" style={iS} /></div>
              <div style={{ marginBottom: 14 }}><label style={lS}>Confirm password</label><input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" style={iS} /></div>
              <FlashMsg msg={passwordMessage} />
              <button onClick={handlePasswordChange} disabled={passwordSaving || !newPassword} style={{ ...btnPrimary, opacity: (passwordSaving || !newPassword) ? 0.3 : 1 }}>{passwordSaving ? "Updating..." : "Update password"}</button>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
}
